import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ShieldCheck, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Persistent banner shown across the app when an admin is impersonating
 * another user. Sits under the main header/mobile bar so it never blocks
 * content. `user.impersonator` (populated by the backend when the JWT
 * carries an impersonator_id claim) drives visibility.
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || !user.impersonator) return null;

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/admin/stop-impersonation");
      await refresh();
      toast.success(`Back to your admin account: ${user.impersonator.email}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not restore admin session.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-40 w-full flex items-center justify-between gap-3 px-4 md:px-6 py-2 text-xs"
      style={{
        background: "linear-gradient(180deg, rgba(232,207,154,0.28), rgba(232,207,154,0.14))",
        borderBottom: "1px solid rgba(232,207,154,0.55)",
        color: "#0b0b0d",
        backdropFilter: "blur(16px)",
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <ShieldCheck className="w-4 h-4 shrink-0" strokeWidth={1.75} />
        <div className="truncate">
          <span className="font-semibold">Signed in as {user.name || user.email}</span>
          <span className="opacity-70"> : admin session held for {user.impersonator.email}</span>
        </div>
      </div>
      <button
        onClick={stop}
        disabled={busy}
        data-testid="stop-impersonation"
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em] transition-opacity disabled:opacity-60"
        style={{
          background: "#0b0b0d",
          color: "#f2dfb5",
        }}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
        Return to admin
      </button>
    </div>
  );
}
